import {
 useState, useEffect, useCallback, useMemo 
} from 'react';
import { useAttributePreference } from '../../common/util/preferences';

// Chave do localStorage para persistir configurações de sessão
const STORAGE_KEY = 'mapControlsSettings';
const FILTER_PANELS_STORAGE_KEY = 'mapFilterPanelsState';

// Evento customizado para notificar mudanças
export const MAP_CONTROLS_CHANGE_EVENT = 'mapControlsChange';
export const FILTER_PANEL_CHANGE_EVENT = 'filterPanelChange';

/**
 * Recupera estado dos painéis de filtro do localStorage
 */
const getFilterPanelsState = () => {
  try {
    const stored = localStorage.getItem(FILTER_PANELS_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.warn('Erro ao ler estado dos painéis de filtro:', e);
  }
  return { poi: false, geofence: false };
};

/**
 * Salva estado dos painéis de filtro no localStorage
 */
const saveFilterPanelsState = (state) => {
  try {
    localStorage.setItem(FILTER_PANELS_STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    console.warn('Erro ao salvar estado dos painéis de filtro:', e);
  }
};

/**
 * Recupera override de sessão do localStorage
 * Retorna apenas as propriedades que foram explicitamente alteradas na sessão
 */
const getSessionOverrides = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.warn('Erro ao ler configurações do mapa:', e);
  }
  return {};
};

/**
 * Salva override de sessão e dispara evento
 */
const saveSessionOverride = (key, value) => {
  try {
    const current = getSessionOverrides();
    const updated = { ...current, [key]: value };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    // Use setTimeout to dispatch event after render completes
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent(MAP_CONTROLS_CHANGE_EVENT, {
        detail: { key, value },
      }));
    }, 0);
  } catch (e) {
    console.warn('Erro ao salvar configurações do mapa:', e);
  }
};

/**
 * Hook para gerenciar os controles do mapa
 *
 * HIERARQUIA DE VALORES (ordem de prioridade):
 * 1. Override de sessão (localStorage) - alterações temporárias do usuário
 * 2. Preferências do usuário (user.attributes) - configurações salvas
 * 3. Valores padrão - fallback
 *
 * Isso permite toggles rápidos sem chamadas à API, mas respeita
 * as preferências salvas do usuário como base.
 */
const useMapControls = () => {
  // Preferências do usuário (user.attributes) - seguindo padrão Traccar
  const userMapCluster = useAttributePreference('mapCluster', true);
  const userMapLiveRoutes = useAttributePreference('mapLiveRoutes', 'none');
  const userMapGeofences = useAttributePreference('mapGeofences', true);
  const poiLayer = useAttributePreference('poiLayer');
  const selectedMapOverlay = useAttributePreference('selectedMapOverlay');

  // Estado local das configurações - inicializado uma vez
  const [settings, setSettings] = useState(() => {
    const overrides = getSessionOverrides();
    return {
      clustering: overrides.clustering ?? userMapCluster,
      showDevices: overrides.showDevices ?? true,
      showGeofences: overrides.showGeofences ?? userMapGeofences,
      showRoutes: overrides.showRoutes ?? false,
      showPoi: overrides.showPoi ?? true,
      showLabels: overrides.showLabels ?? true,
      showTail: overrides.showTail ?? (userMapLiveRoutes !== 'none'),
      showTraffic: overrides.showTraffic ?? Boolean(selectedMapOverlay),
    };
  });

  // NÃO usar useEffect para atualizar settings baseado em preferências
  // Isso causava loop infinito. As preferências só são usadas como valor inicial.

  // Toggle para uma configuração (salva como override de sessão)
  const toggleSetting = useCallback((key) => {
    setSettings((prev) => {
      const newValue = !prev[key];
      saveSessionOverride(key, newValue);
      return { ...prev, [key]: newValue };
    });
  }, []);

  // Atualiza uma configuração específica
  const updateSetting = useCallback((key, value) => {
    setSettings((prev) => {
      saveSessionOverride(key, value);
      return { ...prev, [key]: value };
    });
  }, []);

  // Fit bounds - centraliza todos os dispositivos
  const fitBounds = useCallback(() => {
    window.dispatchEvent(new CustomEvent('mapFitBounds'));
  }, []);

  // Verifica disponibilidade de cada feature
  const availability = useMemo(() => ({
    clustering: true,
    showDevices: true,
    showGeofences: true,
    showRoutes: false, // TODO: implementar rotas salvas
    showPoi: Boolean(poiLayer),
    showLabels: true,
    showTail: true,
    showTraffic: Boolean(selectedMapOverlay),
  }), [poiLayer, selectedMapOverlay]);

  // Estado dos painéis de filtro (persistido no localStorage)
  const [filterPanels, setFilterPanels] = useState(() => getFilterPanelsState());

  // Sincroniza estado quando painéis são fechados externamente (pelo botão X)
  useEffect(() => {
    const handleExternalChange = (event) => {
      setFilterPanels(event.detail);
    };

    window.addEventListener(FILTER_PANEL_CHANGE_EVENT, handleExternalChange);
    return () => {
      window.removeEventListener(FILTER_PANEL_CHANGE_EVENT, handleExternalChange);
    };
  }, []);

  // Toggle painel de filtro (mantém outros abertos)
  const toggleFilterPanel = useCallback((panel) => {
    setFilterPanels((prev) => {
      const newState = {
        ...prev,
        [panel]: !prev[panel], // Apenas alterna o painel clicado
      };
      // Persiste no localStorage
      saveFilterPanelsState(newState);
      // Dispara evento para outros componentes
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent(FILTER_PANEL_CHANGE_EVENT, {
          detail: newState,
        }));
      }, 0);
      return newState;
    });
  }, []);

  // Fecha painel de filtro específico
  const closeFilterPanel = useCallback((panel) => {
    setFilterPanels((prev) => {
      const newState = { ...prev, [panel]: false };
      // Persiste no localStorage
      saveFilterPanelsState(newState);
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent(FILTER_PANEL_CHANGE_EVENT, {
          detail: newState,
        }));
      }, 0);
      return newState;
    });
  }, []);

  return {
    settings,
    updateSetting,
    toggleSetting,
    fitBounds,
    availability,
    filterPanels,
    toggleFilterPanel,
    closeFilterPanel,
  };
};

/**
 * Hook para observar uma única propriedade do controle do mapa
 * Mais eficiente quando você só precisa de uma propriedade específica
 *
 * @param {string} key - Nome da propriedade (ex: 'showLabels', 'showPoi')
 * @param {*} defaultValue - Valor padrão se não encontrar
 * @returns {*} O valor atual da propriedade
 */
/**
 * Hook para observar o estado dos painéis de filtro
 * @returns {{ poi: boolean, geofence: boolean, closeFilterPanel: function }}
 */
export const useFilterPanels = () => {
  const [filterPanels, setFilterPanels] = useState(() => getFilterPanelsState());

  useEffect(() => {
    const handleChange = (event) => {
      setFilterPanels(event.detail);
    };

    window.addEventListener(FILTER_PANEL_CHANGE_EVENT, handleChange);
    return () => {
      window.removeEventListener(FILTER_PANEL_CHANGE_EVENT, handleChange);
    };
  }, []);

  const closeFilterPanel = useCallback((panel) => {
    const newState = { ...filterPanels, [panel]: false };
    setFilterPanels(newState);
    // Persiste no localStorage
    saveFilterPanelsState(newState);
    window.dispatchEvent(new CustomEvent(FILTER_PANEL_CHANGE_EVENT, {
      detail: newState,
    }));
  }, [filterPanels]);

  return { ...filterPanels, closeFilterPanel };
};

export const useMapControlSetting = (key, defaultValue) => {
  const [value, setValue] = useState(() => {
    const overrides = getSessionOverrides();
    return overrides[key] ?? defaultValue;
  });

  useEffect(() => {
    const handleChange = (event) => {
      // O evento agora envia { key, value } para a propriedade específica
      if (event.detail.key === key) {
        setValue((prev) => (prev !== event.detail.value ? event.detail.value : prev));
      }
    };

    window.addEventListener(MAP_CONTROLS_CHANGE_EVENT, handleChange);
    return () => {
      window.removeEventListener(MAP_CONTROLS_CHANGE_EVENT, handleChange);
    };
  }, [key]);

  return value;
};

export default useMapControls;
